
import { useState, lazy, Suspense, useEffect, useId } from "react";
import { ShoppingCart, Trash2, Star, MoreVertical, Palette, Pencil, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShoppingList } from "@/types";
const ShareListDialog = lazy(() => import("@/components/shopping/share/ShareListDialog"));
const RenameListDialog = lazy(() => import("@/components/shopping/lists/RenameListDialog"));
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import usePremiumFeatures from "@/hooks/usePremiumFeatures";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { handleGlobalError } from "@/hooks/useErrorHandler";
import { networkService } from "@/services/network.service";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { safeToastError } from "@/components/ui/toastManager";

interface ListCardProps {
  list: ShoppingList;
  onDelete: (id: string) => void;
  onSetPrimary: (id: string) => void;
  onChangeBackground?: (id: string) => void;
  onOpenList?: (id: string, mode: 'prepare' | 'shop') => void;
  showSetPrimaryOption: boolean;
  isShared?: boolean;
  isNavigating?: boolean;
}

const ListCard = ({ 
  list, 
  onDelete, 
  onSetPrimary, 
  onChangeBackground,
  onOpenList,
  showSetPrimaryOption, 
  isShared = false,
  isNavigating = false
}: ListCardProps) => {
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const { theme } = useThemeConfig();
  const prepDescId = useId();
  const spesaDescId = useId();
  
  // ✅ FIX: Usa il callback onOpenList invece di navigate diretto
  const handleOpenList = (mode: 'prepare' | 'shop') => {
    if (onOpenList && !isNavigating) {
      onOpenList(list.id, mode);
    }
  };
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteClick = () => {
    if (isConfirmDelete) {
      onDelete(list.id);
      setIsConfirmDelete(false);
    } else {
      setIsConfirmDelete(true);
    }
  };

  const { tryUseFeature } = usePremiumFeatures();

  const handleChangeBackground = () => {
    const allowed = tryUseFeature('list_backgrounds', { showToast: true });
    if (!allowed) return;
    if (onChangeBackground) {
      onChangeBackground(list.id);
    }
  };

  // Calculate counts
  const totalItems = list.items?.length || 0;
  const itemsLeft = list.items?.filter(item => !item.checked).length || 0;

  // Style for the card background using theme values. The custom background
  // gradient is now shown only as a 10px strip on the left side, so the main
  // card background always uses the theme card color.
  const cardStyle = {
    borderRadius: theme.cardBorderRadius,
    boxShadow: theme.cardShadow,
    color: theme.sectionTitleColor,
    backgroundColor: theme.cardBackgroundColor
  } as React.CSSProperties;

  // Prefetch the ShareListDialog on mount to avoid network errors
  useEffect(() => {
    import("@/components/shopping/share/ShareListDialog").catch((error) => {
      if (!networkService.isOnline()) {
        safeToastError("Connessione assente", {
          description: "Funzione di condivisione non disponibile offline",
        });
      } else {
        handleGlobalError(error, "Errore nel caricamento della condivisione");
      }
    });
  }, []);

  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

  return (
    <Card
      className="tutorial-target-list-card overflow-hidden relative"
      style={cardStyle}
    >
      {list.backgroundGradient && list.backgroundGradient.trim() !== '' && (
        <div
          className="absolute left-0 top-0 h-full w-[12px]"
          style={{ background: list.backgroundGradient }}
        />
      )}
      <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center">
          <h3 className="text-lg font-medium truncate mr-2">
            {list.name}
          </h3>
          {list.isPrimary && <Star className="h-4 w-4 text-yellow-500" fill="currentColor" />}
        </div>

        <div className="tutorial-target-list-options">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="Apri menu opzioni"
              variant="ghost"
              size="icon"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isShared && (
              <>
                {showSetPrimaryOption && (
                  <DropdownMenuItem onClick={() => onSetPrimary(list.id)}>
                    <Star className="h-4 w-4 mr-2" />
                    <span>Imposta come principale</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleChangeBackground}>
                  <Palette className="h-4 w-4 mr-2" />
                  <span>Colore Barra</span>
                  <Badge
                    variant="secondary"
                    className="ml-2 text-xs"
                    style={{
                      backgroundColor: theme.primaryColor,
                      color: theme.textPrimaryColor,
                      borderRadius: theme.badgeBorderRadius,
                      padding: theme.badgePadding,
                      fontSize: theme.badgeFontSize,
                    }}
                  >
                    Premium
                  </Badge>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsRenameDialogOpen(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  <span>Rinomina</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ color: theme.errorColor }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  <span>Elimina</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-2">
        <div className="text-sm" style={{ color: theme.sectionTextColor }}>
          {totalItems === 0 ? (
            "Nessun articolo"
          ) : (
            <>
              {totalItems} {totalItems === 1 ? "articolo" : "articoli"} • {itemsLeft} da comprare
            </>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex">
        <div className="w-1/2 pr-1">
          <Button
            onClick={() => handleOpenList('prepare')}
            variant="default"
            className="w-full font-bold hover:bg-[var(--btn-bg-hover)]"
            disabled={isNavigating}
            aria-describedby={prepDescId}
            style={{
              backgroundColor: theme.primaryColor,
              color: theme.textPrimaryColor || theme.textColor,
              '--btn-bg-hover': theme.primaryColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding
            } as React.CSSProperties}
          >
            <ListChecks className="h-4 w-4 mr-2" />
            {isNavigating ? '...' : 'Prepara spesa'}
          </Button>
        </div>
        <div className="w-1/2 pl-1">
          <Button
            onClick={() => handleOpenList('shop')}
            variant="default"
            className="w-full font-bold hover:bg-[var(--btn-bg-hover)]"
            disabled={isNavigating}
            aria-describedby={spesaDescId}
            style={{
              backgroundColor: theme.primaryColor,
              color: theme.textPrimaryColor || theme.textColor,
              '--btn-bg-hover': theme.primaryColor,
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding
            } as React.CSSProperties}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {isNavigating ? '...' : 'Fai la spesa'}
          </Button>
        </div>
      </CardFooter>
      <span id={prepDescId} className="sr-only">
        Visualizza i prodotti organizzati per categoria prima della spesa.
      </span>
      <span id={spesaDescId} className="sr-only">
        Visualizza la lista come elenco di prodotti da comprare.
      </span>

      <ErrorBoundary
        onError={(err) =>
          handleGlobalError(err, "Errore nel caricamento della condivisione")
        }
        fallback={
          <div className="p-4 text-center" style={{ color: theme.errorColor }}>
            Errore di caricamento
          </div>
        }
      >
        <Suspense
          fallback={
            <div className="p-4 flex justify-center">
              <Loader2
                className="h-4 w-4 animate-spin"
                style={{ color: theme.primaryColor }}
              />
            </div>
          }
        >
          <ShareListDialog
            open={isShareDialogOpen}
            onOpenChange={setIsShareDialogOpen}
            listId={list.id}
            listName={list.name}
          />
        </Suspense>
      </ErrorBoundary>

      <ErrorBoundary
        onError={(err) =>
          handleGlobalError(err, "Errore nel caricamento della rinomina")
        }
        fallback={
          <div className="p-4 text-center" style={{ color: theme.errorColor }}>
            Errore di caricamento
          </div>
        }
      >
        <Suspense
          fallback={
            <div className="p-4 flex justify-center">
              <Loader2
                className="h-4 w-4 animate-spin"
                style={{ color: theme.primaryColor }}
              />
            </div>
          }
        >
          <RenameListDialog
            open={isRenameDialogOpen}
            onOpenChange={setIsRenameDialogOpen}
            listId={list.id}
            currentName={list.name}
          />
        </Suspense>
      </ErrorBoundary>

      {showDeleteConfirm && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent style={{ backgroundColor: theme.backgroundColor }}>
            <AlertDialogHeader>
              <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.sectionTextColor }}>
                Questa azione è irreversibile. L’elemento sarà rimosso definitivamente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  onDelete(list.id);
                  setShowDeleteConfirm(false);
                }}
              >
                Conferma
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
};

export default ListCard;
